import { TIMEZONE_OPTIONS } from "../constants/campaignConstants";

export function getDefaultCampaignTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONE_OPTIONS.some((o) => o.value === tz)) return tz;
    return "America/New_York";
  } catch {
    return "America/New_York";
  }
}

export function formatDateWithTimezone(date, timezone) {
  if (date == null) return "";
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (Number.isNaN(d.getTime())) return "";
    const tz = timezone || "America/New_York";
    const formatted = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
    const shortTz = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })
      .formatToParts(d)
      .find((p) => p.type === "timeZoneName")?.value || "";
    return `${formatted} ${shortTz}`.trim();
  } catch {
    try {
      const d = typeof date === "string" ? new Date(date) : date;
      return `${d.toLocaleDateString()} at ${d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } catch {
      return "";
    }
  }
}

export function formatScheduleDate(date, timezone) {
  if (!date) return "";
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || "America/New_York",
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(d);
  } catch {
    return "";
  }
}

export function formatScheduleTime(date, timezone) {
  if (!date) return "";
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (Number.isNaN(d.getTime())) return "";
    const tz = timezone || "America/New_York";
    const time = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
    const tzAbbr = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })
      .formatToParts(d)
      .find((p) => p.type === "timeZoneName")?.value || "";
    return `${time}${tzAbbr ? ` ${tzAbbr}` : ""}`;
  } catch {
    return "";
  }
}

/** Read date/time parts for a UTC instant in an IANA timezone. */
function getZonedParts(date, timeZone) {
  const d = typeof date === "string" ? new Date(date) : date;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone || "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parseInt(parts.find((p) => p.type === type)?.value || "0", 10);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour") % 24,
    minute: get("minute"),
  };
}

function compareZonedParts(a, b) {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  if (a.day !== b.day) return a.day - b.day;
  if (a.hour !== b.hour) return a.hour - b.hour;
  return a.minute - b.minute;
}

/**
 * Format a UTC instant as a datetime-local value (YYYY-MM-DDTHH:mm)
 * in the organizer's selected campaign timezone.
 */
export function toTimezoneDateTimeValue(date, timeZone) {
  const parts = getZonedParts(date, timeZone);
  if (!parts) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

/**
 * Parse a datetime-local wall time as belonging to the given IANA timezone.
 * Returns a UTC Date for API storage.
 */
export function parseTimezoneDateTimeValue(value, timeZone) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;
  const wanted = {
    year: parseInt(match[1], 10),
    month: parseInt(match[2], 10),
    day: parseInt(match[3], 10),
    hour: parseInt(match[4], 10),
    minute: parseInt(match[5], 10),
  };
  const tz = timeZone || "America/New_York";
  const roughUtc = Date.UTC(wanted.year, wanted.month - 1, wanted.day, wanted.hour, wanted.minute);
  let lo = roughUtc - 28 * 60 * 60 * 1000;
  let hi = roughUtc + 28 * 60 * 60 * 1000;

  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const parts = getZonedParts(mid, tz);
    if (!parts) return null;
    const cmp = compareZonedParts(parts, wanted);
    if (cmp < 0) lo = mid + 1;
    else hi = mid;
  }

  const result = new Date(lo);
  const finalParts = getZonedParts(result, tz);
  if (!finalParts || compareZonedParts(finalParts, wanted) !== 0) return null;
  return result;
}

export function getDurationLabel(startDate, endDate) {
  const s = typeof startDate === "string" ? new Date(startDate) : startDate;
  const e = typeof endDate === "string" ? new Date(endDate) : endDate;
  if (!(s instanceof Date) || !(e instanceof Date)) return "";
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return "";

  const durationMs = e - s;
  const minutes = Math.floor(durationMs / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days} day${days !== 1 ? "s" : ""}`;
  if (hours > 0) return `${hours} hour${hours !== 1 ? "s" : ""}`;
  return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
}
